'use client';

import { useEffect, useCallback, useState, type FC } from 'react';
import {
  Alert,
  Card,
  Details,
  Flex,
  Heading,
  Pill,
  UploadBox,
  Button,
} from '@/_components';
import { MultiFormUploadConfirmationDialog } from './components/MultiFormUploadConfirmationDialog';
import { UploadedFile } from './components/UploadedFile';
import { getUniqueFiles } from '@/_utils/files';
import type { ExtractedDocumentItem } from '@/_types/models/documents';
import type { BulkUploadProcessorProps } from './BulkUploadProcessor.types';
import { BULK_UPLOAD_PROCESSOR_LINK } from '@/_constants/help-center';

export const BulkUploadProcessor: FC<BulkUploadProcessorProps> = ({
  questionnaire,
  reloadQuestionnairePreview,
  isBulkUploadProcessing,
  setIsBulkUploadProcessing,
  isBUPReviewDialogOpenForSource,
  setIsBUPReviewDialogOpenForSource,
}) => {
  const [bulkDocuments, setBulkDocuments] = useState<
    Array<ExtractedDocumentItem>
  >([]);

  const [
    isMultiFormUploadConfirmationDialogOpen,
    setIsMultiFormUploadConfirmationDialogOpen,
  ] = useState<boolean>(false);
  const [filesUploaded, setFilesUploaded] = useState<Array<File>>([]);

  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      const newFiles = getUniqueFiles(filesUploaded, acceptedFiles);

      setFilesUploaded(newFiles);
      setIsMultiFormUploadConfirmationDialogOpen(true);
    },
    [filesUploaded],
  );

  useEffect(() => {
    if (questionnaire?.extracted_documents) {
      const newDocuments = questionnaire.extracted_documents.filter(
        (doc) =>
          doc.document_type === 'bulk_document' &&
          doc.source.toLowerCase().endsWith('.pdf'),
      );

      setBulkDocuments(newDocuments);
    }
  }, [questionnaire?.extracted_documents]);

  const hasDocuments: boolean = !!bulkDocuments?.length;

  const onClose = () => {
    setIsMultiFormUploadConfirmationDialogOpen(false);
    setFilesUploaded([]);
  };

  const handleHelpCenterLink = () => {
    window.open(BULK_UPLOAD_PROCESSOR_LINK, '_blank');
  };

  return (
    <>
      <MultiFormUploadConfirmationDialog
        filesUploaded={filesUploaded}
        isOpen={isMultiFormUploadConfirmationDialogOpen}
        onClose={onClose}
        questionnaire={questionnaire}
        reloadQuestionnairePreview={reloadQuestionnairePreview}
        setUploading={setIsBulkUploadProcessing}
      />

      <Card padding={10} gap={10}>
        <Flex isBetween={true}>
          <Heading level={3} className="bup">
            Bulk Upload Processor <Pill intent="info" isRound label="Beta" />
          </Heading>
          <Button
            value="How it works"
            intent="ghost"
            size="small"
            iconLeft="book-open-text"
            onClick={handleHelpCenterLink}
          />
        </Flex>
        <Alert type="info" icon="info">
          The Bulk Upload Processor tool splits multiple documents into
          individual ones and matches them with checklist items. Soraban AI may
          make errors, so review processed pages. This flow might change based
          on feedback.
        </Alert>
        {!questionnaire?.locked ? (
          <UploadBox
            onDrop={onDrop}
            isLoading={isBulkUploadProcessing}
            allowMultiple={true}
          />
        ) : (
          <Alert type="warning">
            Documents cannot be uploaded via Bulk Upload Processor while the
            questionnaire is locked.
          </Alert>
        )}
        {hasDocuments && (
          <Details
            title={`Uploaded Files (${bulkDocuments?.length ?? 0})`}
            isOpen
          >
            <Flex isColumn={true} gap={2} padding={2}>
              {bulkDocuments.map((doc) => (
                <UploadedFile
                  documentsRecognized={
                    questionnaire?.extracted_documents?.filter(
                      (ed: ExtractedDocumentItem) =>
                        ed.parent_document_id === doc.id,
                    ) || []
                  }
                  key={doc.id}
                  uploadedFile={doc}
                  questionnaire={questionnaire}
                  reloadQuestionnairePreview={reloadQuestionnairePreview}
                  isBUPReviewDialogOpenForSource={
                    isBUPReviewDialogOpenForSource
                  }
                  setIsBUPReviewDialogOpenForSource={
                    setIsBUPReviewDialogOpenForSource
                  }
                />
              ))}
            </Flex>
          </Details>
        )}
      </Card>
    </>
  );
};
